import { PlaybackState } from "../music/appleMusic";
import { rampVolume } from "./audioOutput";
import type { Block } from "./blocks";
import type { Output } from "./engine";

/**
 * Apple Music tracks through MusicKit JS, one song per queue as on the phone. A track
 * counts as started only once MusicKit reports it playing, and only a track that did
 * start can end: MusicKit also reports the previous song ending when the queue is
 * replaced, and that must not advance the station.
 */
export class MusicKitOutput implements Output {
  onEnded: (() => void) | null = null;
  private generation = 0;
  /** The generation whose song MusicKit has actually played; only its end counts. */
  private armed: number | null = null;
  private waiters: ((state: number) => void)[] = [];

  constructor(private readonly kit: MusicKit.MusicKitInstance) {
    kit.addEventListener("playbackStateDidChange", ((event: { state: number }) =>
      this.handleState(event.state)) as (event: never) => void);
  }

  get position(): number {
    return this.kit.currentPlaybackTime || 0;
  }

  get duration(): number {
    return this.kit.currentPlaybackDuration || 0;
  }

  async start(block: Block): Promise<void> {
    if (block.kind !== "track") throw new Error("not a track block");
    const generation = ++this.generation;
    this.armed = null;
    this.kit.volume = 1;
    await this.kit.setQueue({ song: block.playableID, startPlaying: false });
    if (generation !== this.generation) throw new Error("superseded");
    const playing = this.nextState((state) => state === PlaybackState.playing);
    await this.kit.play();
    await playing;
    if (generation !== this.generation) throw new Error("superseded");
    this.armed = generation;
  }

  pause(): void {
    this.kit.pause();
  }

  resume(): Promise<void> {
    return this.kit.play();
  }

  stop(): void {
    this.generation += 1;
    this.armed = null;
    this.waiters.length = 0;
    void this.kit.stop().catch(() => undefined);
  }

  fade(toVolume: number, ms: number): Promise<void> {
    return rampVolume(
      () => this.kit.volume,
      (volume) => {
        this.kit.volume = Math.min(1, Math.max(0, volume));
      },
      toVolume,
      ms,
    );
  }

  private handleState(state: number): void {
    for (const waiter of this.waiters.splice(0)) waiter(state);
    const ended = state === PlaybackState.ended || state === PlaybackState.completed;
    if (ended && this.armed === this.generation) {
      this.armed = null;
      this.onEnded?.();
    }
  }

  /** Resolves on the next state change that `matches` — a change, never the state the
   * player is already in, which may still be the previous song's. The engine's start
   * timeout bounds the wait. */
  private nextState(matches: (state: number) => boolean): Promise<void> {
    return new Promise((resolve) => {
      const waiter = (state: number) => {
        if (matches(state)) resolve();
        else this.waiters.push(waiter);
      };
      this.waiters.push(waiter);
    });
  }
}
